export const flattenTrackingEntries = (tracking) => {
  if (!tracking) return [];

  if (!Array.isArray(tracking)) {
    if (typeof tracking === 'object') {
      const nestedArrays = Object.values(tracking).filter((value) => Array.isArray(value));
      if (nestedArrays.length) {
        return nestedArrays.flatMap((entry) => flattenTrackingEntries(entry));
      }
      return [tracking];
    }
    return [];
  }

  return tracking.reduce((acc, entry) => {
    if (Array.isArray(entry)) {
      acc.push(...flattenTrackingEntries(entry));
    } else {
      acc.push(entry);
    }
    return acc;
  }, []);
};

export const extractLiveTrackingData = (liveTrackingItem) => {
  if (!liveTrackingItem) return [];

  const sources = [
    liveTrackingItem.tracking,
    liveTrackingItem.config,
    liveTrackingItem.locations,
    liveTrackingItem.path,
    liveTrackingItem.route,
    liveTrackingItem.points,
    liveTrackingItem.tracking?.config,
    liveTrackingItem.tracking?.locations,
    liveTrackingItem.tracking?.path,
    liveTrackingItem.tracking?.route,
  ];

  const merged = [];
  sources.filter(Boolean).forEach((source) => {
    merged.push(...flattenTrackingEntries(source));
  });

  if (merged.length) {
    return merged;
  }

  return flattenTrackingEntries(liveTrackingItem);
};

export const normalizeUserTrackingLocations = (locations) => {
  if (!locations) return [];

  if (Array.isArray(locations)) {
    if (locations.length === 1 && Array.isArray(locations[0])) {
      return flattenTrackingEntries(locations[0]);
    }
    return flattenTrackingEntries(locations);
  }

  if (typeof locations === 'object' && Array.isArray(locations.config)) {
    return flattenTrackingEntries(locations.config);
  }

  return flattenTrackingEntries(locations);
};

export const countTrackablePoints = (tracking) => flattenTrackingEntries(tracking).length;

const getTrackingTimestamp = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const candidates = [
    entry.timestamp,
    entry.time,
    entry.created_at,
    entry.createdAt,
    entry.recorded_at,
    entry.recordedAt,
    entry.date,
    entry.datetime,
  ];

  for (let index = 0; index < candidates.length; index += 1) {
    const value = candidates[index];
    // eslint-disable-next-line no-continue
    if (value === undefined || value === null || value === '') continue;

    const parsed = new Date(value).getTime();
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  return null;
};

export const sortTrackingEntries = (entries) => {
  const flattened = flattenTrackingEntries(entries);
  const hasTimestamps = flattened.some((entry) => getTrackingTimestamp(entry) !== null);

  if (!hasTimestamps) {
    return flattened;
  }

  return [...flattened].sort((first, second) => {
    const firstTime = getTrackingTimestamp(first) ?? 0;
    const secondTime = getTrackingTimestamp(second) ?? 0;
    return firstTime - secondTime;
  });
};

const getTrackingCoordinateKey = (entry) => {
  if (!entry || typeof entry !== 'object') return null;

  const latCandidates = [entry.lat, entry.latitude, entry?.location?.lat, entry?.location?.latitude];
  const lngCandidates = [
    entry.long,
    entry.lng,
    entry.lon,
    entry.longitude,
    entry?.location?.long,
    entry?.location?.lng,
  ];

  const lat = Number(latCandidates.find((value) => value !== undefined && value !== null));
  const lng = Number(lngCandidates.find((value) => value !== undefined && value !== null));

  if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }

  return `${lat.toFixed(6)}:${lng.toFixed(6)}:${getTrackingTimestamp(entry) ?? ''}`;
};

export const mergeTrackingData = (primary, fallback) => {
  const merged = [];
  const seen = new Set();

  sortTrackingEntries([...(primary || []), ...(fallback || [])]).forEach((entry) => {
    const key = getTrackingCoordinateKey(entry) || JSON.stringify(entry);
    if (seen.has(key)) return;
    seen.add(key);
    merged.push(entry);
  });

  return merged;
};

export const shouldFetchDetailedTracking = (tracking, history) => {
  const pointCount = countTrackablePoints(tracking);
  const traveledDistance = Number(history?.google_distance || 0) > 0 || Number(history?.total_distance || 0) > 0;

  return pointCount <= 2 || traveledDistance;
};
